// Plating cards (M16): the built-in cards plus the restaurant's own (IndexedDB 'epb-plating'), duplicate,
// edit, delete own cards, export / import a card file, and PNG / PDF for the kitchen wall.
import { create } from 'zustand';
import { createStore, get, set, type UseStore } from 'idb-keyval';
import { PDFDocument } from 'pdf-lib';
import { BUILT_IN_CARDS, type PlatingCard } from './cards';
import { CARD_H, CARD_W, drawCard, type CardLang } from './drawCard';

let db: UseStore | null = null;
const store = () => (db ??= createStore('epb-plating', 'cards'));
const KEY = 'mine';

type PlatingState = { mine: PlatingCard[]; loaded: boolean; selected: string; lang: CardLang };
export const usePlating = create<PlatingState>(() => ({
  mine: [],
  loaded: false,
  selected: BUILT_IN_CARDS[0]!.id,
  lang: 'es',
}));

export const allCards = (): PlatingCard[] => [...BUILT_IN_CARDS, ...usePlating.getState().mine];
export const cardById = (id: string) => allCards().find((c) => c.id === id);

export async function loadCards() {
  if (usePlating.getState().loaded) return;
  let mine: PlatingCard[] = [];
  try {
    mine = (await get<PlatingCard[]>(KEY, store())) ?? [];
  } catch {
    /* storage unavailable */
  }
  usePlating.setState({ mine, loaded: true });
}
async function persist(mine: PlatingCard[]) {
  usePlating.setState({ mine });
  try {
    await set(KEY, mine, store());
  } catch {
    /* storage unavailable (private window, tests): the cards last for this session */
  }
}

const newId = () => `mine-${Date.now().toString(36)}-${Math.floor(Math.random() * 1e6).toString(36)}`;

/** A copy the restaurant can edit (named after a menu dish later). */
export async function duplicateCard(id: string): Promise<string | null> {
  const c = cardById(id);
  if (!c) return null;
  const copy: PlatingCard = {
    ...structuredClone(c),
    id: newId(),
    builtIn: false,
    name: { en: `${c.name.en} (copy)`, es: `${c.name.es} (copia)` },
    updatedAt: new Date().toISOString(),
  };
  await persist([...usePlating.getState().mine, copy]);
  usePlating.setState({ selected: copy.id });
  return copy.id;
}

export async function updateCard(id: string, fn: (c: PlatingCard) => void) {
  const mine = usePlating.getState().mine.map((c) => {
    if (c.id !== id) return c;
    const n = structuredClone(c);
    fn(n);
    n.updatedAt = new Date().toISOString();
    return n;
  });
  await persist(mine);
}

export async function deleteCard(id: string) {
  await persist(usePlating.getState().mine.filter((c) => c.id !== id));
  usePlating.setState({ selected: BUILT_IN_CARDS[0]!.id });
}

// ───────────── files ─────────────

const toB64 = (u: Uint8Array) => {
  let s = '';
  for (let i = 0; i < u.length; i += 0x8000) s += String.fromCharCode(...u.subarray(i, i + 0x8000));
  return btoa(s);
};

/** A card as a file (`.epbplate.json`), photo included. */
export function cardFile(c: PlatingCard): string {
  const { photo, ...rest } = c;
  return JSON.stringify(
    {
      app: 'eat-photo-booth',
      kind: 'plating-card',
      v: 1,
      card: { ...rest, photo: photo ? { type: photo.type, data: toB64(photo.bytes) } : null },
    },
    null,
    1,
  );
}

/** Reads a card file; throws on anything else. The imported card is a new own card. */
export async function importCardFile(text: string): Promise<string> {
  const raw = JSON.parse(text) as { app?: string; kind?: string; v?: number; card?: Record<string, unknown> };
  if (raw.app !== 'eat-photo-booth' || raw.kind !== 'plating-card' || !raw.card)
    throw new Error('not a plating card file');
  if ((raw.v ?? 0) > 1) throw new Error('made by a newer version of the app');
  const c = raw.card as unknown as PlatingCard & { photo: { type: string; data: string } | null };
  if (!c.name?.en || !Array.isArray(c.elements) || !Array.isArray(c.steps))
    throw new Error('card is incomplete');
  const card: PlatingCard = {
    ...c,
    id: newId(),
    builtIn: false,
    photo: c.photo
      ? { type: c.photo.type, bytes: Uint8Array.from(atob(c.photo.data), (ch) => ch.charCodeAt(0)) }
      : null,
  };
  await persist([...usePlating.getState().mine, card]);
  return card.id;
}

// ───────────── export ─────────────

async function photoBitmap(c: PlatingCard) {
  return c.photo
    ? createImageBitmap(new Blob([c.photo.bytes as Uint8Array<ArrayBuffer>], { type: c.photo.type }))
    : null;
}

/** The card as PNG, Letter proportions at `dpi` (150 → 1275 × 1650). */
export async function cardPng(c: PlatingCard, lang: CardLang, dpi = 150): Promise<Uint8Array> {
  const k = dpi / 150;
  const cv = new OffscreenCanvas(Math.round(CARD_W * k), Math.round(CARD_H * k));
  const photo = await photoBitmap(c);
  drawCard(cv.getContext('2d')!, c, lang, cv.width, cv.height, photo);
  photo?.close();
  return new Uint8Array(await (await cv.convertToBlob({ type: 'image/png' })).arrayBuffer());
}

/** The card as a one-page Letter PDF (the 200 dpi PNG placed full page). */
export async function cardPdf(c: PlatingCard, lang: CardLang): Promise<Uint8Array> {
  const pdf = await PDFDocument.create();
  pdf.setTitle(lang === 'en' ? c.name.en : c.name.es);
  pdf.setCreator('EAT Photo Booth');
  const page = pdf.addPage([612, 792]);
  const img = await pdf.embedPng(await cardPng(c, lang, 200));
  page.drawImage(img, { x: 0, y: 0, width: 612, height: 792 });
  return pdf.save();
}
